import { useEffect, useState } from 'react'
import { Paper, Typography, useTheme } from '@mui/material'
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded'
import dayjs from 'dayjs'
import ClinicBrandPayoutReports from 'src/clinic/views/clinicBrandPayoutReports/ClinicBrandPayoutReports'
import { buildClinicPayoutReportsDoctor } from 'src/clinic/views/clinicBrandPayoutReports/util'
import { CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import { EVENT_TYPE_CONSULTATION, EVENT_TYPE_THERAPY_SESSION, EVENT_TYPES_ARRAY } from 'src/consumer/constants'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import ExportPayoutReportsDialogWithButton from 'src/common/components/payoutReports/ExportPayoutReportsDialogWithButton'
import { PaymentConfigMenuWithDialog } from 'src/supportPortal/components/common/paymentConfig/PaymentConfigExample'
import { PayoutReportsFiltersRoot } from './payoutReportsStyles'

function defaultReportDateRange() {
  const base = dayjs()
  return {
    start: base.subtract(1, 'month').startOf('month'),
    end: base.subtract(1, 'month').endOf('month'),
  }
}

export default function ClinicPayoutReports({ clinicBrandData, hideClinicBrandDropDown = false }) {
  const theme = useTheme()
  const [selectedClinicBrand, setSelectedClinicBrand] = useState(clinicBrandData)
  const [selectedEventType, setSelectedEventType] = useState(
    EVENT_TYPES_ARRAY?.find((eType) => eType?.id === EVENT_TYPE_THERAPY_SESSION),
  )
  const [loading, setLoading] = useState(false)
  const [allClinicBrands, setAllClinicBrands] = useState()
  const [eventTypes, setEventTypes] = useState([])
  const [reportDate, setReportDate] = useState(() => defaultReportDateRange().start)
  const [reportEndDate, setReportEndDate] = useState(() => defaultReportDateRange().end)

  function handleDateRangeChange(start, end) {
    if (start) setReportDate(start)
    if (end) setReportEndDate(end)
  }

  async function getAllClinicBrands() {
    const resp = await getSPDataWithParams(`/api/secure/cc/support/clinic-brand/list`, {})
    if (resp.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const brands = getDataMapFromCommonResponse(resp)?.['clinic-brand']
        setAllClinicBrands(brands)
      }
    }
  }

  async function getAllEventTypes() {
    const resp = await getSPDataWithParams(`/api/secure/cc/support/reference/event-types`, {})
    if (resp.status === 200) {
      setEventTypes(resp?.data?.sort((a, b) => a?.title?.localeCompare(b?.title)))
    }
  }

  async function setup() {
    setLoading(true)
    const setupTasks = [getAllEventTypes()]
    if (!hideClinicBrandDropDown) {
      setupTasks.push(getAllClinicBrands())
    }
    await Promise.all(setupTasks)
    setLoading(false)
  }

  useEffect(() => {
    setup()
  }, [])

  useEffect(() => {
    setSelectedClinicBrand(clinicBrandData)
  }, [clinicBrandData])

  const clinicBrandTitle = selectedClinicBrand?.brandName || selectedClinicBrand?.title || ''
  const pageHeading = clinicBrandTitle
    ? clinicBrandTitle
    : 'Clinic Payout Reports'
  const pageSubHeading = clinicBrandTitle
    ? 'Payout Reports'
    : undefined

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <SupportHeader heading={pageHeading} subHeading={pageSubHeading} />
      <PayoutReportsFiltersRoot>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            maxWidth: 1600,
            width: '100%',
            mx: 'auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: theme.spacing(2),
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(1.5),
                flex: 1,
                minWidth: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                <FilterListRoundedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                  Filters
                </Typography>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: theme.spacing(2),
                  alignItems: 'flex-end',
                }}
              >
                {!hideClinicBrandDropDown && (
                  <div style={{ minWidth: 220 }}>
                    <CustomSelect
                      value={selectedClinicBrand}
                      options={allClinicBrands?.sort(
                        (a, b) =>
                          a?.brandName?.localeCompare(b?.brandName) ||
                          a?.title?.localeCompare(b?.title),
                      )}
                      label="Clinic Brand"
                      getOptionValue={(optn) => optn?.id}
                      getOptionLabel={(optn) => optn?.brandName || optn?.title}
                      selectType={selectTypes.object}
                      onChangeEvent={(e) => setSelectedClinicBrand(e.target.value)}
                      id="clinicBrand"
                      isLoading={loading}
                    />
                  </div>
                )}
                <div style={{ minWidth: 200 }}>
                  <CustomSelect
                    value={selectedEventType}
                    getOptionValue={(optn) => optn?.id}
                    getOptionLabel={(optn) => optn?.title}
                    options={eventTypes}
                    label="Event type"
                    selectType={selectTypes.object}
                    onChangeEvent={(e) => setSelectedEventType(e.target.value)}
                    id="eventType"
                    isLoading={loading}
                  />
                </div>
              </div>
            </div>
            {selectedClinicBrand?.id && (
              <div
                style={{
                  display: 'flex',
                  gap: theme.spacing(1),
                  alignItems: 'flex-start',
                  flexShrink: 0,
                }}
              >
                <ExportPayoutReportsDialogWithButton
                  dataFetchingApiUrl="/api/secure/cc/support/payout-reports/clinic-brand/paginated"
                  startDate={reportDate}
                  endDate={reportEndDate}
                  entityId={selectedClinicBrand?.id}
                  entityType="clinic-brand"
                  eventType={selectedEventType?.id}
                  allEventTypes={eventTypes}
                  fileNamePrefix={
                    selectedEventType?.id === EVENT_TYPE_CONSULTATION
                      ? 'Consultation_Payout_Reports'
                      : 'Therapy_Payout_Reports'
                  }
                  extraParams={{ clinicBrandId: selectedClinicBrand?.id }}
                  payoutReportDataArrayToExcelFormatArrayBuilder={buildClinicPayoutReportsDoctor}
                />
                <PaymentConfigMenuWithDialog
                  payoutEntityId={selectedClinicBrand?.id}
                  payoutEntityType="clinic-brand"
                  entityName="Clinic Brand"
                />
              </div>
            )}
          </div>
        </Paper>
      </PayoutReportsFiltersRoot>
      {selectedClinicBrand && selectedClinicBrand?.id ? (
        <ClinicBrandPayoutReports
          apiUrl={'/api/secure/cc/support/payout-reports/clinic-brand/paginated'}
          eventType={selectedEventType.id}
          clinicBrandId={selectedClinicBrand?.id}
          thisClinicBrandData={selectedClinicBrand}
          showSummaryReport
          hideBackButton
          summaryApiUrl="/api/secure/cc/support/payout-reports/clinic-brand/summary"
          isForServiceProvider
          showAdminActions
          allEventTypes={eventTypes}
          controlledReportDate={reportDate}
          controlledReportEndDate={reportEndDate}
          onDateRangeChange={handleDateRangeChange}
          hideExportAndPaymentInToolbar
        />
      ) : (
        <div
          style={{
            padding: theme.spacing(4, 3),
            textAlign: 'center',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Select a clinic brand to view payout reports
          </Typography>
        </div>
      )}
    </div>
  )
}
